import React, { useEffect, useRef, useState } from 'react';
import {ContractExceptionFields} from './ContractExceptionFields';
import { getCrewList, getCrewListForDropdown, getRanks, getRanksforList, signOnCrew } from '../core/_requests';
import { toast } from 'react-toastify';
import { Vessel } from '../../operations/core/_models';
import { Crew, Rank } from '../core/_models';

import { useAuth } from '../../auth';
import { useCrewingRoleAccess } from '../core/crewingRoleAccess';
import { KTSVG } from '../../../../_metronic/helpers';
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
const app_link = process.env.REACT_APP_URL;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the API response after a successful sign-on. */
  onSignedOn: (data: {
    id: number;
    vesselId: number;
    crewId: number;
    signOnDate: string;
    signOnTime?: string | null;
    portSignOn: string;
    status: string;
    loginLink: string;
  }) => void;
}

const SignOnModal: React.FC<Props> = ({ onClose, isOpen, onSignedOn }) => {
  const { currentUser } = useAuth();
  const {
    isCrew,
    isOperator: baseIsOperator,
    isOfficeRole,
    isCompanyAdmin,
    isCompanyGroupAdmin,
  } = useCrewingRoleAccess();
  const roleId = currentUser?.role?.id;
  const roleEntityId = currentUser?.roleEntityId;
  const operatorCompanyAdminId = currentUser?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? null
  const operatorCompanyGroupAdminId = currentUser?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id ?? null
  // Operator flavors:
  // - Has companyAdminId => acts like Company Admin
  // - Else has companyGroupAdminId => acts like Company Group Admin
  // - Else => acts like Superadmin
  console.log(app_link);
  
  const isOperator = baseIsOperator || roleId === 6
  const operatorActsLikeCompanyAdmin = isOperator && operatorCompanyAdminId != null
  const operatorActsLikeGroupAdmin = isOperator && !operatorActsLikeCompanyAdmin && operatorCompanyGroupAdminId != null
  const operatorActsLikeSuperadmin = isOperator && !operatorActsLikeCompanyAdmin && operatorCompanyGroupAdminId == null

  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [crews, setCrews] = useState<Crew[]>([]);
  const [ranks, setRanks] = useState<Rank[]>([]);
  const [rankMap, setRankMap] = useState<Record<number, string>>({});

  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('');
  const [selectedCrewId, setSelectedCrewId] = useState<number | ''>('');
  const [signOnPort, setSignOnPort] = useState('');
  const [dateOfAgreement, setDateOfAgreement] = useState('');
  const [contractEndDate, setContractEndDate] = useState('');
  const [signOnDate, setSignOnDate] = useState('');
  const [signOnTime, setSignOnTime] = useState(() =>
    new Date().toISOString().slice(11, 16)
  );
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLock = useRef(false);
  const [contractBufferException, setContractBufferException] = useState(false);
  const [contractBufferExceptionReason, setContractBufferExceptionReason] = useState('');
  useEffect(() => {
    setContractBufferException(false);
    setContractBufferExceptionReason('');
  }, [selectedCrewId, selectedVesselId, contractEndDate, signOnDate, isOpen]);


  // A crew is "already on a vessel" if either vesselId or vessel?.id is truthy.
  const hasVessel = (c: Crew) => Boolean((c as any).vesselId || c?.vessel?.id);
  const isUnassigned = (c: Crew) => !hasVessel(c);
  const normalizeDateOnly = (value?: string | null): string | null => {
    if (!value) return null;
    const trimmed = String(value).trim();
    return trimmed.length >= 10 ? trimmed.slice(0, 10) : null;
  };

  // Keep only approved AND unassigned
  const onlyApproved = (list: Crew[]) =>
    list.filter(c => c.approvalStatus === 'APPROVED' && isUnassigned(c));

  const fetchCrewForSignOn = async (): Promise<Crew[]> => {
    // Crew sign-on relies on dropdown endpoint; /all is vessel scoped for crew role.
    if (isCrew || roleId === 4) {
      return getCrewListForDropdown();
    }
    return getCrewList();
  };


  useEffect(() => {
    // 1️⃣ vessels
    fetchVessels();
    // 2️⃣ crews
    fetchCrewForSignOn()
      .then(list => setCrews(onlyApproved(list)))
      .catch(err => {
        console.error(err);
        showErrorToast(err, 'Failed to load available crew members.', 'load');
      });

    console.log("crews aye: ", crews)

    console.log("currentUser: ", currentUser)

    // 3️⃣ ranks → build map
    getRanksforList()
      .then(rs => {
        setRanks(rs);
        const m: Record<number, string> = {};
        rs.forEach(r => (m[r.id] = r.rank));
        setRankMap(m);
      }).catch(err => {
        console.error(err);
        showErrorToast(err, 'Failed to load ranks.', 'load');
      });

    if (isCrew || roleId === 4) {
      const vesselFromAuth = currentUser?.vessel;
      if (vesselFromAuth) {
        setSelectedVesselId(vesselFromAuth.id);
        // handleVesselChange(vesselFromAuth.id); // Directly filter crew for role 4
      }
    }

  }, [roleId, isCrew, roleEntityId, operatorActsLikeSuperadmin, operatorActsLikeGroupAdmin, operatorActsLikeCompanyAdmin, operatorCompanyAdminId, operatorCompanyGroupAdminId]);

  useEffect(() => {
    if ((isCrew || roleId === 4) && currentUser?.vessel?.id && vessels.length > 0) {
      setSelectedVesselId(currentUser.vessel.id);
      filterCrewsByVessel(currentUser.vessel.id);
    }
  }, [roleId, isCrew, currentUser?.vessel?.id, vessels.length]);




  const fetchVessels = async () => {
    try {
      const vesselList = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser());
      setVessels(vesselList);
    } catch (error) {
      console.error('Failed to fetch vessel list:', error);
      showErrorToast(error, 'Failed to load vessel list.', 'load');
    }
  };


  // For role 4, directly filter the crews based on vessel
  const filterCrewsByVessel = async (vesselId: number) => {
    const selectedVessel = vessels.find(v => v.id === vesselId);
    if (selectedVessel) {
      const companyAdminId = selectedVessel.companyAdmin?.id;
      const companyGroupAdminId = selectedVessel.companyGroupAdmin?.id;

      try {
        const crewList = await fetchCrewForSignOn(); // Await the assignable crew list here
        const filteredCrews = onlyApproved(crewList).filter(crew => {
          if (companyGroupAdminId && crew.companyGroupAdminId === companyGroupAdminId) {
            return true;
          }
          if (companyAdminId && crew.companyAdminId === companyAdminId) {
            return true;
          }
          return false;
        })
          .filter(isUnassigned);
        setCrews(filteredCrews);

        // Safety: clear selection if it’s not in the approved list
        if (!filteredCrews.some(c => c.id === Number(selectedCrewId))) {
          setSelectedCrewId('');
        }
      } catch (error) {
        console.error('Failed to fetch crew list:', error);
      }
    }
  };


  if (!isOpen) return null;

  // When a vessel is selected, filter the crew based on the vessel's associated company or sub-company
  const handleVesselChange = async (vesselId: number) => {
    setSelectedVesselId(vesselId);

    if (!vesselId) {
      setCrews([]); // Reset crews if no vessel is selected
      return;
    }

    const selectedVessel = vessels.find(v => v.id === vesselId);
    if (selectedVessel) {
      const companyAdminId = selectedVessel.companyAdmin?.id;
      const companyGroupAdminId = selectedVessel.companyGroupAdmin?.id;

      try {
        const crewList = await fetchCrewForSignOn(); // Fetch the assignable crew list
        const filteredCrews = onlyApproved(crewList).filter(crew => {
          // Filter crew based on the selected vessel's company or sub-company
          if (companyGroupAdminId && crew.companyGroupAdminId === companyGroupAdminId) {
            return true;
          }
          if (companyAdminId && crew.companyAdminId === companyAdminId) {
            return true;
          }
          return false;
        })
          .filter(isUnassigned);  // ← exclude crews who already have a vessel
        setCrews(filteredCrews);

        // If previously selected crew is no longer available (not approved or not in company), clear it
        if (!filteredCrews.some(c => c.id === Number(selectedCrewId))) {
          setSelectedCrewId('');
        }
      } catch (error) {
        console.error('Failed to fetch crew list:', error);
      }
    }
  };

  const handleSubmit = async () => {
    if (submitLock.current) return;
    if (contractBufferException && (!contractBufferExceptionReason.trim() || contractBufferExceptionReason.trim().length > 1000)) {
      toast.warn('Enter document exception remarks of 1 to 1000 characters.');
      return;
    }
    if (roleId !== 4 && !selectedVesselId) {
      toast.warn('Please select a vessel.');
      return;
    }
    if (!selectedCrewId) {
      toast.warn('Please select a crew member.');
      return;
    }
    if (!signOnPort?.trim()) {
      toast.warn('Please enter the sign on port.');
      return;
    }
    if (!dateOfAgreement) {
      toast.warn('Please select the date of agreement.');
      return;
    }
    if (!contractEndDate) {
      toast.warn('Please select the contract end date.');
      return;
    }
    if (!signOnDate) {
      toast.warn('Please select the sign on date.');
      return;
    }
    if (!signOnTime) {
      toast.warn('Please enter the sign on time.');
      return;
    }
    if (contractEndDate < signOnDate) {
      toast.warn('Contract end date cannot be earlier than sign-on date.');
      return;
    }

    setError('');

    const selectedCrewObj = crews.find(c => c.id === Number(selectedCrewId));
    const crewAvailabilityDate = normalizeDateOnly(selectedCrewObj?.dateOfAvailability);
    const selectedSignOnDate = normalizeDateOnly(signOnDate);

    if (crewAvailabilityDate && selectedSignOnDate && selectedSignOnDate < crewAvailabilityDate) {
      const msg = `Sign-on date cannot be before crew availability date (${crewAvailabilityDate}).`;
      setError(msg);
      toast.warn(msg);
      return;
    }

    // For crew role, use the vessel from currentUser, else use selectedVesselId
    const vesselIdToSend = (isCrew || roleId === 4) ? currentUser?.vessel?.id : selectedVesselId;

    if (!vesselIdToSend) {
      toast.warn('Vessel is required.');
      return;
    }

    try {
      submitLock.current = true;
      setIsSubmitting(true);
      const payload = {
        vesselId: Number(vesselIdToSend),
        crewId: Number(selectedCrewId),
        signOnDate,            // in "YYYY-MM-DD" format
        dateOfAgreement,
        contractEndDate,
        signOnTime,
        portSignOn: signOnPort.trim(),
        contractBufferException,
        contractBufferExceptionReason: contractBufferException ? contractBufferExceptionReason.trim() : undefined
      };

      const resp = await signOnCrew(payload);

      const vesselForLink =
        (isCrew || roleId === 4)
          ? currentUser?.vessel
          : vessels.find(v => v.id === Number(selectedVesselId));

      const loginLink = `${app_link}/auth/${vesselForLink?.vesselType}/${vesselForLink?.imoNumber}/ship-login`;

      toast.success('Crew signed on successfully');
      onSignedOn({
        id: resp.id,
        vesselId: resp.vesselId,
        crewId: resp.crewId,
        signOnDate: resp.signOnDate,
        signOnTime: resp.signOnTime ?? signOnTime,
        portSignOn: resp.portSignOn,
        status: resp.status,
        loginLink,
      });
      onClose();
    } catch (err: any) {
      console.error(err);
      if (err?.response?.data?.message?.includes("is already signed on that vessel")) {
        toast.error(`The selected rank is already assigned to the vessel.`);
      } else {
        showErrorToast(err, 'Failed to sign on crew.', 'create');
      }
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  // Lookup selected crew to show its rank
  const selectedCrew = crews.find(c => c.id === Number(selectedCrewId));


  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '70rem' }}
      >
        <div className="custom-modal-header d-flex justify-content-between align-items-center">
          <h5 className="m-0">Sign On Crew</h5>
          <button className="close-btn" onClick={onClose} disabled={isSubmitting}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <div className="custom-modal-body">
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="row gy-4">
            {/* For roleId 4, hide Vessel selection */}
            {roleId !== 4 && (
              <div className="col-md-6">
                <label className="modal_label">Select Vessel <span className="text-danger">*</span></label>
                <SearchableSelect
                  className="form-select"
                  value={selectedVesselId}
                  onChange={e => handleVesselChange(Number(e.target.value))}
                >
                  <option value="">-- Select Vessel --</option>
                  {vessels.map(v => (
                    <option key={v.id} value={v.id}>{v.fleet_name}</option>
                  ))}
                </SearchableSelect>
              </div>
            )}

            {/* 2) Crew */}
            <div className="col-md-6">
              <label className="modal_label" >Select Crew <span className="text-danger">*</span></label>
              <SearchableSelect
                className="form-select"
                value={selectedCrewId}
                onChange={e => setSelectedCrewId(Number(e.target.value))}
                disabled={!selectedVesselId && roleId !== 4}// Disable crew selection until a vessel is selected, except for role 4
              >
                <option value="">-- Select Crew --</option>
                {crews.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </SearchableSelect>
            </div>

            {/* 3) Rank (read-only) */}
            <div className="col-md-6">
              <label className='modal_label' htmlFor="rank">Crew Rank <span className="text-danger">*</span></label>

              <input
                type="text"
                className="form-control"
                readOnly
                value={selectedCrew ? (selectedCrew as any)?.rank?.rank || (selectedCrew as any)?.rankName || rankMap[selectedCrew.rankId] || '' : ''}
                placeholder="—"
              />
            </div>

            {/* 4) Sign On Port */}
            <div className="col-md-6">
              <label className="modal_label">Sign On Port <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={signOnPort}
                onChange={e => setSignOnPort(e.target.value)}
              />
            </div>

            {/* 4.5) Date of Agreement */}
            <div className="col-md-6">
              <label className="modal_label">Date of Agreement <span className="text-danger">*</span></label>
              <input
                type="date"
                className="form-control"
                value={dateOfAgreement}
                onChange={e => setDateOfAgreement(e.target.value)}
              />
            </div>

            {/* 4.6) Contract End Date */}
            <div className="col-md-6">
              <label className="modal_label">Contract End Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className="form-control"
                value={contractEndDate}
                onChange={e => setContractEndDate(e.target.value)}
              />
            </div>

            {/* 5) Sign On Date */}
            <div className="col-md-6">
              <label className="modal_label">Sign On Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className="form-control"
                value={signOnDate}
                min={selectedCrew?.dateOfAvailability ? String(selectedCrew.dateOfAvailability).slice(0, 10) : undefined}
                onChange={e => setSignOnDate(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="modal_label">Sign On Time <span className="text-danger">*</span></label>
              <input
                type="time"
                className="form-control"
                value={signOnTime}
                onChange={e => setSignOnTime(e.target.value)}
              />
            </div>
          </div>
        </div>

        <ContractExceptionFields checked={contractBufferException} reason={contractBufferExceptionReason}
          onChecked={setContractBufferException} onReason={setContractBufferExceptionReason} disabled={isSubmitting} />
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn_secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button className="btn btn_success" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Signing On...
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div >
  );
};

export default SignOnModal;
